import {
  HHButton,
  HHError,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import { DataGridPro, GridPaginationModel } from '@mui/x-data-grid-pro';
import { useCallback, useState } from 'react';
import { useDynamicDialogContext } from '../../context/dynamic-dialog.context';
import { useDynamicDrawerContext } from '../../context/dynamic-drawer.context';
import { useSnackbarContext } from '../../context/snackbar.context';
import { useBillingWriteAccess } from '../../hooks/use-billing-write-access';
import {
  GetAllPackagesDocument,
  PackageOfferingEnum,
  PackageType,
  useDeletePackageMutation,
  useGetAllProductsQuery,
  useGetAllProgramIndicationsQuery,
  useUpdatePackageMutation,
} from '../../types';
import { capitalizeFirstLetterAndSpaceSnakeString } from '../../utils/bills-utils';
import { NoRowsOverlay } from '../grid-components/no-rows-or-error-overlay';
import { PackageForm } from './package-form/form';
import { PackageFormValues } from './package-form/validation-schema';
import { usePackageColumns } from './use-package-columns';
import { usePackageQueries } from './use-package-queries';

export const PACKAGE_GRID_TEST_ID = 'package-grid';

const DEFAULT_PAGE_SIZE = 50;

const toPackageFormValues = (row: PackageType): PackageFormValues => ({
  name: row.name,
  offeringType: row.packageOfferings.some(o => o.packageOffering === 'product')
    ? PackageOfferingEnum.Product
    : PackageOfferingEnum.ProgramIndication,
  programIndications: row.packageOfferings
    .filter(
      o => o.packageOffering === 'program_indication' && o.programIndication,
    )
    .map(o => o.programIndication as string),
  productId:
    row.packageOfferings.find(o => o.packageOffering === 'product')
      ?.productId ?? '',
});

export const PackageGrid = (): JSX.Element => {
  const canWrite = useBillingWriteAccess();
  const { rows, loading, hasError } = usePackageQueries();
  const { setDialogContent, closeDialog } = useDynamicDialogContext();
  const { setDrawerContent, onClose } = useDynamicDrawerContext();
  const { setSnackbarState } = useSnackbarContext();
  const { data: productsData } = useGetAllProductsQuery();

  // PackageForm calls this same query. Firing it here on grid mount means
  // the data is already in Apollo's cache when the Edit/View drawer opens,
  // so the form renders instantly instead of showing "Loading indications..."
  useGetAllProgramIndicationsQuery();

  const [updatePackage] = useUpdatePackageMutation({
    refetchQueries: [{ query: GetAllPackagesDocument }],
  });
  const [deletePackage] = useDeletePackageMutation({
    refetchQueries: [{ query: GetAllPackagesDocument }],
  });

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const handleEdit = useCallback(
    (row: PackageType): void => {
      const initialValues = toPackageFormValues(row);

      const handleUpdate = async (values: PackageFormValues): Promise<void> => {
        try {
          await updatePackage({
            variables: {
              packageId: row.id,
              packageInput: {
                name: values.name.trim(),
                packageOffering: values.offeringType,
                programIndications:
                  values.offeringType === PackageOfferingEnum.ProgramIndication
                    ? values.programIndications
                    : null,
                productId:
                  values.offeringType === PackageOfferingEnum.Product
                    ? (values.productId as number)
                    : null,
              },
            },
          });
          setSnackbarState('Package updated successfully', 'success', 3000);
          onClose();
        } catch {
          setSnackbarState('Error updating package', 'error', 3000);
        }
      };

      setDrawerContent(
        <HHStack padding={4}>
          <PackageForm
            onSubmit={handleUpdate}
            initialValues={initialValues}
            packageId={row.id}
            existingPackages={rows}
            isEditable={row.isEditable === true}
          />
        </HHStack>,
        <HHTypography hhVariant="h6">Edit Package</HHTypography>,
        '35%',
        'temporary',
      );
    },
    [setDrawerContent, updatePackage, setSnackbarState, onClose, rows],
  );

  const handleView = useCallback(
    (row: PackageType): void => {
      setDrawerContent(
        <HHStack padding={4}>
          <PackageForm
            initialValues={toPackageFormValues(row)}
            isEditable={row.isEditable === true}
            readOnly
            onSubmit={(): void => undefined}
            existingPackages={rows}
            packageId={row.id}
          />
        </HHStack>,
        <HHTypography hhVariant="h6">
          {capitalizeFirstLetterAndSpaceSnakeString(row.name)}
        </HHTypography>,
        '35%',
        'temporary',
      );
    },
    [setDrawerContent, rows],
  );

  const handleDelete = useCallback(
    (row: PackageType): void => {
      const executeDelete = async (): Promise<void> => {
        closeDialog();
        try {
          await deletePackage({ variables: { packageId: row.id } });
          setSnackbarState('Package deleted successfully', 'success', 3000);
        } catch {
          setSnackbarState('Error deleting package', 'error', 3000);
        }
      };

      setDialogContent({
        title: (
          <HHTypography hhVariant="section-title">Delete Package</HHTypography>
        ),
        content: (
          <HHStack spacing={2}>
            <HHTypography hhVariant="body1">
              Are you sure you want to delete the package &quot;
              {capitalizeFirstLetterAndSpaceSnakeString(row.name)}&quot;?
            </HHTypography>
            <HHTypography hhVariant="caption" color="text.secondary">
              This will also remove all associated package offerings.
            </HHTypography>
          </HHStack>
        ),
        footer: (
          <HHStack direction="row" spacing={2} padding={1}>
            <HHButton
              hhVariant="variant-bypass"
              variant="outlined"
              onClick={closeDialog}
              data-testid="package-delete-cancel-button"
            >
              Cancel
            </HHButton>
            <HHButton
              hhVariant="variant-bypass"
              variant="contained"
              color="error"
              onClick={(): void => {
                executeDelete().catch(() => undefined);
              }}
              data-testid="package-delete-confirm-button"
            >
              Delete
            </HHButton>
          </HHStack>
        ),
        width: 'sm',
        fullWidth: true,
      });
    },
    [setDialogContent, closeDialog, deletePackage, setSnackbarState],
  );

  const columns = usePackageColumns({
    onEdit: handleEdit,
    onView: handleView,
    onDelete: handleDelete,
    products: productsData?.getAllProducts,
    canWrite,
  });

  if (hasError) {
    return (
      <HHError body="Error fetching packages" hhVariant="variant-bypass" />
    );
  }

  return (
    <HHStack
      flex={1}
      direction="column"
      spacing={6}
      justifyContent="space-between"
      minHeight="0"
    >
      <DataGridPro
        columns={columns}
        rows={rows}
        loading={loading}
        getRowId={(row): number => row.id}
        pagination
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        paginationMode="client"
        initialState={{
          sorting: { sortModel: [{ field: 'name', sort: 'asc' }] },
        }}
        sortingMode="client"
        filterMode="client"
        pageSizeOptions={[25, 50, 100]}
        disableRowSelectionOnClick
        pinnedColumns={{ right: ['actions'] }}
        aria-label="Packages table"
        data-testid={PACKAGE_GRID_TEST_ID}
        {...(process.env.NODE_ENV === 'test' && {
          disableVirtualization: true,
        })}
        slots={{
          noResultsOverlay: NoRowsOverlay,
          noRowsOverlay: NoRowsOverlay,
        }}
        slotProps={{
          noResultsOverlay: { error: hasError } as Record<string, boolean>,
          noRowsOverlay: { error: hasError } as Record<string, boolean>,
        }}
      />
    </HHStack>
  );
};
